import React from "react";
import { Alert, Badge, Button, Col, Form, Row } from "react-bootstrap";
import {
  AdminEdmContextEntity,
  AdminEdmContextRepresentation,
  AdminEdmContextValidationDetail,
  AdminEdmPolicy,
} from "../../../types/adminEdm";
import workspaceTheme from "../../../components/artwork-workspace/workspaceTheme.module.css";
import { EdmFieldLabel } from "./EdmFieldLabel";
import { createEmptyContextEntity, getPrimaryContextLabel, normalizeContextEntityForForm } from "./edm-authoring.shared";

type Props = {
  contexts: AdminEdmContextEntity[];
  disabled?: boolean;
  representations?: AdminEdmContextRepresentation[];
  validationByIndex?: Map<number, AdminEdmContextValidationDetail[]>;
  supportedVocabularies?: AdminEdmPolicy["supportedVocabularies"];
  saveDisabled?: boolean;
  onSaveRequested?: () => void;
  onChange: (next: AdminEdmContextEntity[]) => void;
};

const STATUS_LABELS: Record<string, { label: string; bg: string }> = {
  "link-only": { label: "Link only", bg: "secondary" },
  represented: { label: "Represented contextual class", bg: "success" },
  "missing-fields": { label: "Missing fields for represented class", bg: "warning" },
};

type ReferenceUriUiStatus = {
  badgeLabel: string;
  badgeBg: string;
  alertVariant: "secondary" | "warning" | "info";
  message: string;
};

const trimToNull = (value?: string | null): string | null => {
  const normalized = typeof value === "string" ? value.trim() : "";
  return normalized || null;
};

const hasPrimaryLabelValue = (context: AdminEdmContextEntity): boolean =>
  context.prefLabel.some((entry) => Boolean(trimToNull(entry?.value)));

const hasLegacyReferencePayload = (context: AdminEdmContextEntity): boolean =>
  Boolean(
    hasPrimaryLabelValue(context)
    || trimToNull(context.sameAs)
    || trimToNull(context.note)
    || trimToNull(context.begin)
    || trimToNull(context.end)
    || trimToNull(context.exactMatch)
    || trimToNull(context.broader)
    || trimToNull(context.related)
    || (context.lat !== null && context.lat !== undefined)
    || (context.long !== null && context.long !== undefined)
    || trimToNull(context.professionOrOccupation)
    || trimToNull(context.dateOfBirth)
    || trimToNull(context.dateOfDeath)
    || trimToNull(context.placeOfBirth)
    || trimToNull(context.placeOfDeath)
  );

const escapeRegex = (value: string): string =>
  value.replace(/[|\\{}()[\]^$+*?.]/g, "\\$&");

const normalizeSchemePattern = (pattern: string, notes?: string | null): string => {
  const normalizedNotes = trimToNull(notes)?.toLowerCase() || "";
  if (normalizedNotes.includes("http is not supported anymore")) {
    return pattern.replace(/^http:\/\//i, "https://");
  }
  if (/^http:\/\//i.test(pattern)) {
    return pattern.replace(/^http:\/\//i, "https?://");
  }
  return pattern;
};

const buildCanonicalRegex = (pattern: string, notes?: string | null): RegExp | null => {
  const normalizedPattern = trimToNull(pattern);
  if (!normalizedPattern) {
    return null;
  }

  const escaped = escapeRegex(normalizeSchemePattern(normalizedPattern, notes))
    .replace(/^https\\\?:\/\//i, "https?://")
    .replace(/\\\{[^}]+\\\}/g, "[^\\s?#]+");

  return new RegExp(`^${escaped}$`, "i");
};

const getUrlHost = (value?: string | null): string | null => {
  const normalizedValue = trimToNull(value);
  if (!normalizedValue) {
    return null;
  }

  try {
    return new URL(normalizedValue).host.toLowerCase();
  } catch (_error) {
    return null;
  }
};

const getPatternPrefix = (pattern: string): string | null => trimToNull(pattern.split("{")[0]);

const resolveReferenceUriStatus = (
  context: AdminEdmContextEntity,
  representation: AdminEdmContextRepresentation | undefined,
  supportedVocabularies: AdminEdmPolicy["supportedVocabularies"]
): ReferenceUriUiStatus | null => {
  if ((context.mode || "described") !== "reference") {
    return null;
  }

  const referenceUri = trimToNull(context.referenceUri);
  if (!referenceUri) {
    return {
      badgeLabel: "Reference URI missing",
      badgeBg: "secondary",
      alertVariant: "secondary",
      message: "Add a supported authority URI for link mode, or switch to Described resource if Artetra should publish a contextual class.",
    };
  }

  const typeCompatibleEntries = supportedVocabularies.filter((entry) => entry.supportedContextTypes.includes(context.type));
  const canonicalEntry = typeCompatibleEntries.find((entry) =>
    entry.canonicalPatterns.some((pattern) => buildCanonicalRegex(pattern, entry.notes)?.test(referenceUri))
      || entry.exampleUris.includes(referenceUri)
  );

  if (canonicalEntry) {
    if (hasLegacyReferencePayload(context)) {
      return {
        badgeLabel: "Legacy compatibility path",
        badgeBg: "warning",
        alertVariant: "warning",
        message: `This ${canonicalEntry.name} link still includes local contextual fields, so it will keep exporting a contextual class until those fields are cleared or the mode is switched to Described resource.`,
      };
    }

    return {
      badgeLabel: "Supported authority URI",
      badgeBg: "success",
      alertVariant: "info",
      message: `This ${canonicalEntry.name} link will export as a direct authority relation. Switch to Described resource if Artetra should publish the contextual class itself.`,
    };
  }

  const referenceHost = getUrlHost(referenceUri);
  const matchingEntry = typeCompatibleEntries.find((entry) => {
    const prefixes = entry.canonicalPatterns
      .map((pattern) => getPatternPrefix(pattern))
      .filter((pattern): pattern is string => Boolean(pattern));
    const hosts = [
      ...prefixes.map((prefix) => getUrlHost(prefix)).filter((host): host is string => Boolean(host)),
      ...entry.exampleUris.map((uri) => getUrlHost(uri)).filter((host): host is string => Boolean(host)),
    ];

    return prefixes.some((prefix) => referenceUri.startsWith(prefix))
      || (referenceHost ? hosts.includes(referenceHost) : false);
  });

  if (matchingEntry) {
    return {
      badgeLabel: "Fix URI pattern",
      badgeBg: "warning",
      alertVariant: "warning",
      message: `${matchingEntry.name} is supported, but this URI does not match the canonical pattern Europeana expects. Correct the URI or switch to Described resource.`,
    };
  }

  const fallbackStatus = representation?.validationStatus;
  const fallbackMessage = fallbackStatus === "supported_noncanonical"
    ? "This reference URI does not match the canonical pattern Europeana expects."
    : "This reference URI is not in the current Europeana-supported vocabulary list for this context type.";

  return {
    badgeLabel: fallbackStatus === "supported_noncanonical" ? "Fix URI pattern" : "Unsupported vocabulary",
    badgeBg: "warning",
    alertVariant: "warning",
    message: `${fallbackMessage} Use a supported authority URI or switch to Described resource.`,
  };
};

export const EdmContextEntitiesEditor = ({
  contexts,
  disabled = false,
  representations = [],
  validationByIndex,
  supportedVocabularies = [],
  saveDisabled = false,
  onSaveRequested,
  onChange,
}: Props) => {
  const patchContextEntity = (index: number, patch: Partial<AdminEdmContextEntity>) => {
    onChange(contexts.map((context, contextIndex) => (
      contextIndex === index ? normalizeContextEntityForForm({ ...context, ...patch }) : context
    )));
  };

  const patchContextLabel = (index: number, patch: Partial<AdminEdmContextEntity["prefLabel"][number]>) => {
    onChange(contexts.map((context, contextIndex) => {
      if (contextIndex !== index) return context;
      const primaryLabel = getPrimaryContextLabel(context);
      return normalizeContextEntityForForm({
        ...context,
        prefLabel: [{
          ...primaryLabel,
          ...patch,
        }],
      });
    }));
  };

  const addContextEntity = () => {
    onChange([...contexts, createEmptyContextEntity()]);
  };

  const removeContextEntity = (index: number) => {
    onChange(contexts.filter((_, contextIndex) => contextIndex !== index));
  };

  const switchContextMode = (index: number, mode: AdminEdmContextEntity["mode"]) => {
    patchContextEntity(index, {
      mode,
      uri: "",
      referenceUri: "",
    });
  };

  return (
    <div className="d-flex flex-column gap-3">
      {contexts.length === 0 ? <div className="text-white-50">No EDM contexts configured yet.</div> : null}
      {contexts.map((context, index) => {
        const representation = representations.find((entry) => entry.index === index);
        const status = STATUS_LABELS[representation?.status || "missing-fields"];
        const issues = validationByIndex?.get(index) || [];
        const primaryLabel = getPrimaryContextLabel(context);
        const referenceStatus = resolveReferenceUriStatus(context, representation, supportedVocabularies);
        const showDescriptiveFields = (context.mode || "described") !== "reference" || hasLegacyReferencePayload(context);

        return (
          <div key={`context-${index}`} className="border rounded p-3">
            <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
              <div className="d-flex flex-column gap-2">
                <div className="fw-semibold">Context #{index + 1}</div>
                <div className="d-flex gap-2 flex-wrap">
                  <Badge bg={status.bg as any}>{status.label}</Badge>
                  {referenceStatus ? <Badge bg={referenceStatus.badgeBg as any}>{referenceStatus.badgeLabel}</Badge> : null}
                  {representation?.referenceUri ? <Badge bg="dark">{representation.referenceUri}</Badge> : null}
                </div>
              </div>
              <div className="d-flex gap-2">
                {onSaveRequested ? (
                  <Button
                    variant="light"
                    size="sm"
                    type="button"
                    className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary} ${workspaceTheme.workspaceButtonSmall}`}
                    disabled={disabled || saveDisabled}
                    aria-label={`Save context ${index + 1}`}
                    onClick={onSaveRequested}
                  >
                    Save
                  </Button>
                ) : null}
                <Button
                  variant="light"
                  size="sm"
                  type="button"
                  className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSmall} ${workspaceTheme.cardDangerAction}`}
                  disabled={disabled}
                  onClick={() => removeContextEntity(index)}
                >
                  Remove
                </Button>
              </div>
            </div>

            {issues.length ? (
              <Alert variant="warning">
                {issues.map((issue, issueIndex) => (
                  <div key={`issue-${index}-${issueIndex}`}>{issue.message}</div>
                ))}
              </Alert>
            ) : null}
            {referenceStatus ? (
              <Alert variant={referenceStatus.alertVariant} className="mb-3">
                {referenceStatus.message}
              </Alert>
            ) : null}

            <Row className="g-3">
              <Col md={4}>
                <Form.Label>Type</Form.Label>
                <Form.Select
                  className={workspaceTheme.workspaceSelect}
                  value={context.type}
                  disabled={disabled}
                  onChange={(event) => patchContextEntity(index, {
                    type: event.target.value as AdminEdmContextEntity["type"],
                    providedChoLink: event.target.value === "Agent" ? (context.providedChoLink || null) : null,
                  })}
                >
                  <option value="Agent">Agent</option>
                  <option value="Concept">Concept</option>
                  <option value="Place">Place</option>
                  <option value="TimeSpan">TimeSpan</option>
                </Form.Select>
              </Col>
              <Col md={4}>
                <Form.Label>Mode</Form.Label>
                <Form.Select
                  className={workspaceTheme.workspaceSelect}
                  aria-label={`Context ${index + 1} mode`}
                  value={context.mode}
                  disabled={disabled}
                  onChange={(event) => switchContextMode(index, event.target.value as AdminEdmContextEntity["mode"])}
                >
                  <option value="reference">Reference URI</option>
                  <option value="described">Described resource</option>
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label>{context.mode === "reference" ? "Reference URI" : "Local/context URI"}</Form.Label>
                <Form.Control
                  aria-label={context.mode === "reference" ? `Context ${index + 1} reference URI` : `Context ${index + 1} local context URI`}
                  placeholder={context.mode === "reference" ? "https://example.org/authority/resource" : "https://example.org/context/resource"}
                  value={context.mode === "reference" ? (context.referenceUri || "") : (context.uri || "")}
                  disabled={disabled}
                  onChange={(event) => patchContextEntity(index, context.mode === "reference" ? { referenceUri: event.target.value } : { uri: event.target.value })}
                />
              </Col>

              {showDescriptiveFields ? (
                <>
                  <Col md={4}>
                    <Form.Label>Label language</Form.Label>
                    <Form.Control value={primaryLabel.lang || ""} disabled={disabled} onChange={(event) => patchContextLabel(index, { lang: event.target.value })} />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Pref label</Form.Label>
                    <Form.Control value={primaryLabel.value} disabled={disabled} onChange={(event) => patchContextLabel(index, { value: event.target.value })} />
                  </Col>
                  <Col md={6}>
                    <Form.Label>sameAs</Form.Label>
                    <Form.Control value={context.sameAs || ""} disabled={disabled} onChange={(event) => patchContextEntity(index, { sameAs: event.target.value })} />
                  </Col>
                </>
              ) : null}

              {context.type === "Concept" && showDescriptiveFields ? (
                <>
                  <Col md={6}>
                    <Form.Label>exactMatch</Form.Label>
                    <Form.Control value={context.exactMatch || ""} disabled={disabled} onChange={(event) => patchContextEntity(index, { exactMatch: event.target.value })} />
                  </Col>
                  <Col md={6}>
                    <Form.Label>broader</Form.Label>
                    <Form.Control value={context.broader || ""} disabled={disabled} onChange={(event) => patchContextEntity(index, { broader: event.target.value })} />
                  </Col>
                  <Col md={6}>
                    <Form.Label>related</Form.Label>
                    <Form.Control value={context.related || ""} disabled={disabled} onChange={(event) => patchContextEntity(index, { related: event.target.value })} />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Note</Form.Label>
                    <Form.Control value={context.note || ""} disabled={disabled} onChange={(event) => patchContextEntity(index, { note: event.target.value })} />
                  </Col>
                </>
              ) : null}

              {context.type === "TimeSpan" && showDescriptiveFields ? (
                <>
                  <Col md={6}>
                    <Form.Label>Begin</Form.Label>
                    <Form.Control value={context.begin || ""} disabled={disabled} onChange={(event) => patchContextEntity(index, { begin: event.target.value })} />
                  </Col>
                  <Col md={6}>
                    <Form.Label>End</Form.Label>
                    <Form.Control value={context.end || ""} disabled={disabled} onChange={(event) => patchContextEntity(index, { end: event.target.value })} />
                  </Col>
                </>
              ) : null}

              {context.type === "Place" && showDescriptiveFields ? (
                <>
                  <Col md={6}>
                    <Form.Label>Latitude</Form.Label>
                    <Form.Control value={context.lat ?? ""} disabled={disabled} onChange={(event) => patchContextEntity(index, { lat: event.target.value === "" ? null : Number(event.target.value) })} />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Longitude</Form.Label>
                    <Form.Control value={context.long ?? ""} disabled={disabled} onChange={(event) => patchContextEntity(index, { long: event.target.value === "" ? null : Number(event.target.value) })} />
                  </Col>
                </>
              ) : null}

              {context.type === "Agent" ? (
                <>
                  <Col md={6}>
                    <EdmFieldLabel
                      label="Relation to CHO"
                      as="text"
                      helpText="Choose whether this Agent exports as a linked dc:creator resource, a linked edm:hasMet relation, or stays internal to authoring."
                      tooltipId={`context-agent-link-help-${index}`}
                    />
                    <Form.Select
                      className={workspaceTheme.workspaceSelect}
                      aria-label={`Context ${index + 1} relation to CHO`}
                      value={context.providedChoLink || ""}
                      disabled={disabled}
                      onChange={(event) => patchContextEntity(index, {
                        providedChoLink: event.target.value ? event.target.value as AdminEdmContextEntity["providedChoLink"] : null,
                      })}
                    >
                      <option value="">Not linked</option>
                      <option value="creator">Creator (dc:creator resource)</option>
                      <option value="hasMet">Related agent (edm:hasMet)</option>
                    </Form.Select>
                  </Col>
                  {showDescriptiveFields ? (
                    <>
                      <Col md={6}>
                        <Form.Label>Profession or occupation</Form.Label>
                        <Form.Control value={context.professionOrOccupation || ""} disabled={disabled} onChange={(event) => patchContextEntity(index, { professionOrOccupation: event.target.value })} />
                      </Col>
                      <Col md={6}>
                        <Form.Label>Date of birth</Form.Label>
                        <Form.Control value={context.dateOfBirth || ""} disabled={disabled} onChange={(event) => patchContextEntity(index, { dateOfBirth: event.target.value })} />
                      </Col>
                      <Col md={6}>
                        <Form.Label>Date of death</Form.Label>
                        <Form.Control value={context.dateOfDeath || ""} disabled={disabled} onChange={(event) => patchContextEntity(index, { dateOfDeath: event.target.value })} />
                      </Col>
                      <Col md={6}>
                        <Form.Label>Place of birth</Form.Label>
                        <Form.Control value={context.placeOfBirth || ""} disabled={disabled} onChange={(event) => patchContextEntity(index, { placeOfBirth: event.target.value })} />
                      </Col>
                      <Col md={6}>
                        <Form.Label>Place of death</Form.Label>
                        <Form.Control value={context.placeOfDeath || ""} disabled={disabled} onChange={(event) => patchContextEntity(index, { placeOfDeath: event.target.value })} />
                      </Col>
                    </>
                  ) : null}
                </>
              ) : null}
            </Row>
          </div>
        );
      })}

      <Button
        variant="light"
        size="sm"
        type="button"
        className={`align-self-start ${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary} ${workspaceTheme.workspaceButtonSmall}`}
        disabled={disabled}
        onClick={addContextEntity}
      >
        Add another context manually
      </Button>
    </div>
  );
};

export default EdmContextEntitiesEditor;
